import { cancel, put, take, select, all, race, call } from 'redux-saga/effects';
import get from 'lodash/get';

import { checkForIsSignedAllConsents } from '../../../components/Healthboard/Components/VisitConsents/utils/checkForIsSignedAllConsents';
import { showLoaderInPopup, hideLoaderInPopup } from '../../ui/popup';
import {
  hasProviderProfileSelector,
  targetUserIdSelector,
} from '../../context/selectors';
import * as sucessUI from '../../ui/success';
import { URGENT_VISIT_TYPE_CODE } from '../../../constants';
import { appointmentConsentsModalActions } from '../appointment-consent-modal';
import { selectVisitTestCallPerformed } from '../../data/selectors';
import { appointmentDetailsActions } from '../appointment-details';
import * as changeStatus from '../requests/status-change-req';
import * as changeUrgentVisitStatus from '../requests/status-urgent-visit-change-req';
import * as appointmentDetails from '../../appointmnents/appointment-details-req';
import { actions as errorActions } from '../../ui/error';
import { virtualVisitActions } from '../virtual-visit';
import { selectVirtualVisitActive } from '../selectors';
import * as appState from '../../data/app-state-req';
import { checkPermission } from './checkPermission';
import { CustomEvents, GATrackCustomEvent } from '../../../gtag';
import { preVisitConsentsModalActions } from '../appointment-survey-assignment';
import { STATUS_SURVEY } from '../../../components/Healthboard/Components/VisitSurveyListView/constants';

function alreadyStartedCall() {
  return {
    type: sucessUI.consts.UPDATE,
    payload: { id: 'virtualVisit.error.alreadyInCall' },
    iconType: 'info-active',
  };
}

function* handleChangeRequest(request, payload) {
  yield put(request.actions.send(payload));

  const { error, success = [] } = yield race({
    error: take(request.consts.REJECTED),
    success: all([take(request.consts.SUCCEED), take(request.consts.ADD_META)]),
  });

  const [{ payload: result } = {}, { payload: meta } = {}] = success;
  return { error, result, meta };
}

function* defineChangeRequestByCode({ code, ...payload }) {
  switch (code) {
    case URGENT_VISIT_TYPE_CODE:
      return yield handleChangeRequest(changeUrgentVisitStatus, payload);
    default:
      return yield handleChangeRequest(changeStatus, payload);
  }
}

export function* startVirtualVisit({ id, appointment, event, flow }) {
  const currentUserId = yield select(targetUserIdSelector);
  const isActive = yield select(selectVirtualVisitActive);
  const isProvider = yield select(hasProviderProfileSelector);
  const appointmentId = appointment.id;
  const { code } = appointment.visitType;
  const consentsEntity = appointment.consentsAssignment;
  const isSignedAllConsents = checkForIsSignedAllConsents(appointment);
  const isPreVisitFormRequired = get(appointment, 'requiredSurveys', false);
  const allPrePreVisitForm = get(appointment, 'surveyAssignments', []);
  const notSignedPreVisitForms = allPrePreVisitForm.filter(
    form =>
      ![STATUS_SURVEY.COMPLETED, STATUS_SURVEY.EXPIRED].includes(form.status) &&
      !!form.survey.required,
  );

  if (consentsEntity && !isSignedAllConsents && !isProvider) {
    const allConsents = consentsEntity ? consentsEntity.consents : [];
    const notSignedConsent = allConsents.filter(item => !item.accepted);
    const userId = get(appointment, 'userId');

    const organizationName = consentsEntity.userAppointment.organization.name;
    const serviceName = consentsEntity.userAppointment.service.name;
    const dateTime = consentsEntity.userAppointment.startTime;

    yield put(
      appointmentConsentsModalActions.open({
        consent: notSignedConsent[0],
        organizationName,
        serviceName,
        allConsents,
        startTime: dateTime,
        patientUserInfoId: userId || currentUserId,
      }),
    );
    yield cancel();
  }
  if (isPreVisitFormRequired && notSignedPreVisitForms.length > 0) {
    const nextUnsignedPreVisitForm =
      notSignedPreVisitForms.find(
        form =>
          ![STATUS_SURVEY.COMPLETED, STATUS_SURVEY.EXPIRED].includes(
            form.status,
          ) && !!form.survey.required,
      ) || {};
    if (nextUnsignedPreVisitForm.assignmentId) {
      yield put(
        preVisitConsentsModalActions.setAssignmentId(
          nextUnsignedPreVisitForm.assignmentId,
        ),
      );
      yield put(
        preVisitConsentsModalActions.setSurveys(notSignedPreVisitForms),
      );
      yield cancel();
    }
  }
  if (isActive) {
    yield put(alreadyStartedCall());
    yield cancel();
  }

  yield call(checkPermission);

  yield put(showLoaderInPopup());

  const { error, result, meta } = yield defineChangeRequestByCode({
    id,
    code,
    event,
    appointmentId,
  });

  if (error) {
    yield put(hideLoaderInPopup());
    yield put(errorActions.update({ id: 'errors.common' }));
    yield cancel();
  }

  const visitTestCallPerformed = yield select(selectVisitTestCallPerformed);
  if (!visitTestCallPerformed) {
    yield put(appState.actions.changeTestCallPerformedStatus());
  }

  yield put(
    appointmentDetailsActions.replaceAppointment({
      ...appointment,
      room: result.room,
      status: result.status,
    }),
  );

  yield put(
    virtualVisitActions.setConfig({
      ...meta,
      roomPendingInvitations: get(result, 'roomPendingInvitations'),
      typeCode: get(appointment, 'visitType.code'),
      roomName: get(appointment, 'patientUserInfo.displayableName', ''),
      roomNumber: `VV-${appointmentId}`,
    }),
  );

  yield put(appointmentDetails.actions.succeed(result));
  yield put(virtualVisitActions.setVisibility(true));
  if (flow === 'get-care') {
    yield call(() =>
      GATrackCustomEvent(CustomEvents.GET_CARE_COMPLETE, {
        visitCode: code,
      }),
    );
  }
  yield put(hideLoaderInPopup());
}

export function* handleFailedChangeStatus() {
  yield put(hideLoaderInPopup());
}



// WEBPACK FOOTER //
// ./src/redux/healthboard/sagas/start-virtual-visit-saga.js